import { useContext, useLayoutEffect } from "react";
import { Internals, useBufferState } from "remotion";

/**
 * Hold the Player's timeline while an asset this layer needs is still loading,
 * so playback never runs past a layer that has nothing to paint yet. The
 * built-in media tags do this for themselves: `<Video>` and `<Audio>` from
 * `@remotion/media` natively, `<Img>` behind its `pauseWhenLoading` prop. This
 * is the same behavior for the assets our own layers fetch (SVG and HTML
 * overlay markup, caption tracks).
 *
 * Skipped while the layer is premounted or postmounted, mirroring `<Img>`.
 * Layers premount for the whole composition, so an unguarded handle would hold
 * playback from frame 0 until every asset in the ad had loaded.
 *
 * `SequenceContext` is a Remotion internal, and the nearest Sequence inherits
 * `premounting` from its parent, so a `layout="none"` wrapper between here and
 * the premounted Sequence is transparent. `@remotion/media` reads the flag the
 * same way.
 */
export function usePauseWhileLoading(isLoading: boolean): void {
  const { delayPlayback } = useBufferState();
  const sequenceContext = useContext(Internals.SequenceContext);
  const isPremounting = Boolean(sequenceContext?.premounting);
  const isPostmounting = Boolean(sequenceContext?.postmounting);

  useLayoutEffect(() => {
    if (!isLoading || isPremounting || isPostmounting) {
      return;
    }
    // Releasing through the cleanup covers unmount and a seek out of the
    // premount window, not just the load finishing.
    return delayPlayback().unblock;
  }, [delayPlayback, isLoading, isPremounting, isPostmounting]);
}
